import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Tooltip,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as InvoicedIcon,
  Search as SearchIcon,
  Receipt as InvoiceIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api.service';
import { isPlatformLevel } from '../utils/roleUtils';

const CreateInvoice = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Role check helper
  const isPlatform = isPlatformLevel(currentUser?.role);

  const PAGE_SIZE = 20;

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data states
  const [typingCenters, setTypingCenters] = useState([]);
  const [selectedTypingCenterId, setSelectedTypingCenterId] = useState('');
  const [mappedOrganizations, setMappedOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [invoicedTickets, setInvoicedTickets] = useState([]); // [{ticket_id, invoice_number, invoice_id}]
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceFilter, setInvoiceFilter] = useState('not_invoiced'); // 'all', 'not_invoiced', 'invoiced'
  const [statusFilter, setStatusFilter] = useState(''); // '', 'COMPLETED', 'CLOSED', 'OPEN', etc.

  const [page, setPage] = useState(0);
  const [lastFetchCount, setLastFetchCount] = useState(0);

  const [dateRange, setDateRange] = useState('30_DAYS');
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const getDateRange = () => {
    const formatYYYYMMDD = (date) => date.toISOString().slice(0, 10);
    const now = new Date();
    let startDate = null;
    let endDate = formatYYYYMMDD(now);

    switch (dateRange) {
      case '24_HR':
        startDate = formatYYYYMMDD(new Date(now.getTime() - 24 * 60 * 60 * 1000));
        break;
      case '7_DAYS':
        startDate = formatYYYYMMDD(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
        break;
      case '30_DAYS':
        startDate = formatYYYYMMDD(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
        break;
      case '90_DAYS':
        startDate = formatYYYYMMDD(new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000));
        break;
      case 'CUSTOM':
        startDate = customStartDate || null;
        endDate = customEndDate || formatYYYYMMDD(now);
        break;
      default:
        startDate = formatYYYYMMDD(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
    }

    return { startDate, endDate };
  };

  // Loading states
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [creating, setCreating] = useState(false);
  const [checkingConfig, setCheckingConfig] = useState(false);
  const [hasInvoiceConfig, setHasInvoiceConfig] = useState(null);

  // Fetch typing centers on mount
  useEffect(() => {
    fetchTypingCenters();
  }, []);

  // Fetch organizations and check config when typing center changes
  useEffect(() => {
    if (selectedTypingCenterId) {
      checkInvoiceConfig(selectedTypingCenterId);
      fetchMappedOrganizations(selectedTypingCenterId);
    }
  }, [selectedTypingCenterId]);

  const checkInvoiceConfig = async (typingCenterId) => {
    setCheckingConfig(true);
    try {
      const response = await ApiService.getInvoiceConfig(typingCenterId);
      setHasInvoiceConfig(response.data?.data ? true : false);
    } catch (err) {
      // 404 means no config exists
      if (err.response?.status === 404) {
        setHasInvoiceConfig(false);
      } else {
        console.error('Error checking invoice config:', err);
        setHasInvoiceConfig(false);
      }
    } finally {
      setCheckingConfig(false);
    }
  };

  // Fetch tickets when organization changes
  useEffect(() => {
    if (selectedOrganization) {
      fetchTicketsForOrganization(selectedOrganization.id);
    }
  }, [selectedOrganization, selectedTypingCenterId, dateRange, customStartDate, customEndDate, page]);

  const fetchTypingCenters = async () => {
    try {
      const response = await ApiService.getTypingCenters();
      if (response.data?.data) {
        setTypingCenters(response.data.data);
        // Set default typing center
        if (response.data.data.length > 0) {
          const defaultTc = currentUser?.typingCenterId || response.data.data[0].id;
          setSelectedTypingCenterId(defaultTc);
        }
      }
    } catch (err) {
      console.error('Error fetching typing centers:', err);
      setError('Failed to load typing centers');
    }
  };

  const fetchMappedOrganizations = async (typingCenterId) => {
    setLoadingOrgs(true);
    setMappedOrganizations([]);
    setSelectedOrganization(null);
    setTickets([]);
    setSelectedTickets([]);
    setPage(0);
    try {
      const mappingsResponse = await ApiService.get(`/api/mappings?typingCenterId=${typingCenterId}`);
      if (mappingsResponse.data?.data) {
        const orgs = mappingsResponse.data.data.map(m => ({
          id: m.organization_id,
          name: m.organization_name,
          code: m.organization_code || ''
        }));
        setMappedOrganizations(orgs);

        if (orgs.length === 1) {
          setSelectedOrganization(orgs[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching mapped organizations:', err);
      setError('Failed to load organizations');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const fetchTicketsForOrganization = async (organizationId) => {
    setLoadingTickets(true);
    setSelectedTickets([]);
    try {
      // Fetch tickets for this organization
      const { startDate, endDate } = getDateRange();
      const ticketResponse = await ApiService.getTickets({
        organizationId: organizationId,
        startDate,
        endDate,
        page,
        pageSize: PAGE_SIZE
      });

      // Fetch already invoiced tickets with invoice numbers for this typing center and organization
      const invoicedResponse = await ApiService.getInvoicedTicketIds(organizationId, selectedTypingCenterId);
      const invoicedData = invoicedResponse.data?.data || [];
      setInvoicedTickets(invoicedData);

      if (ticketResponse.data?.data) {
        const payload = ticketResponse.data.data;
        const fetched = Array.isArray(payload)
          ? payload
          : (payload?.tickets || payload?.items || payload?.results || payload?.data || []);

        const fetchCount = typeof ticketResponse.data?.count === 'number'
          ? ticketResponse.data.count
          : (typeof payload?.count === 'number' ? payload.count : fetched.length);

        setTickets(fetched);
        setLastFetchCount(fetchCount);
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError('Failed to load tickets');
      setTickets([]);
      setLastFetchCount(0);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleOrganizationChange = (event) => {
    const orgId = event.target.value;
    const org = mappedOrganizations.find(o => o.id === orgId);
    setSelectedOrganization(org || null);
    setPage(0);
  };

  const handleToggleTicket = (ticketId) => {
    setSelectedTickets(prev => {
      if (prev.includes(ticketId)) {
        return prev.filter(id => id !== ticketId);
      } else {
        return [...prev, ticketId];
      }
    });
  };

  const handleSelectAllAvailable = () => {
    const availableTicketIds = filteredTickets
      .filter(t => !isTicketInvoiced(t.id))
      .map(t => t.id);
    setSelectedTickets(availableTicketIds);
  };

  const handleDeselectAll = () => {
    setSelectedTickets([]);
  };

  const isTicketSelectable = (ticket) => {
    // Allow all ticket statuses - only check if not already invoiced
    return !isTicketInvoiced(ticket.id);
  };

  // Helper to check if a ticket is already invoiced
  // Uses case-insensitive comparison for UUIDs
  const isTicketInvoiced = (ticketId) => {
    if (!ticketId || invoicedTickets.length === 0) return false;
    const ticketIdLower = String(ticketId).toLowerCase();
    return invoicedTickets.some(t => String(t.ticket_id).toLowerCase() === ticketIdLower);
  };

  // Helper to get invoice info for a ticket
  const getInvoiceInfo = (ticketId) => {
    if (!ticketId || invoicedTickets.length === 0) return null;
    const ticketIdLower = String(ticketId).toLowerCase();
    return invoicedTickets.find(t => String(t.ticket_id).toLowerCase() === ticketIdLower);
  };

  const handleCreateInvoice = async () => {
    if (!selectedOrganization) {
      setError('Please select Organization');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      // Get selected tickets data
      const ticketsToAdd = tickets.filter(t => selectedTickets.includes(t.id));

      // Create invoice with tickets in a SINGLE call
      const response = await ApiService.createInvoice({
        typingCenterId: selectedTypingCenterId,
        organizationId: selectedOrganization.id,
        organizationName: selectedOrganization.name,
        organizationCode: selectedOrganization.code || '',
        tickets: ticketsToAdd  // Include tickets in the same call
      });

      if (response.data?.data) {
        const invoiceId = response.data.data.id;
        const skippedCount = response.data.skippedCount || 0;
        const addedCount = response.data.addedCount || ticketsToAdd.length;

        if (skippedCount > 0) {
          setSuccess(`Invoice created with ${addedCount} ticket(s). ${skippedCount} ticket(s) were skipped (already invoiced).`);
        } else {
          setSuccess('Invoice created successfully!');
        }

        setTimeout(() => {
          navigate(`/invoices/${invoiceId}`);
        }, 1500);
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setCreating(false);
    }
  };

  const getTicketStatusChip = (status) => {
    const statusUpper = status?.toUpperCase();
    const statusConfig = {
      COMPLETED: { color: 'success', label: 'Completed' },
      CLOSED: { color: 'info', label: 'Closed' },
      OPEN: { color: 'primary', label: 'Open' },
      IN_PROGRESS: { color: 'warning', label: 'In Progress' },
      ASSIGNED: { color: 'warning', label: 'Assigned' },
      PENDING: { color: 'info', label: 'Pending', variant: 'outlined' },
      CANCELLED: { color: 'error', label: 'Cancelled' }
    };
    const config = statusConfig[statusUpper] || { color: 'info', label: status, variant: 'outlined' };
    return <Chip label={config.label} color={config.color} variant={config.variant} size="small" sx={{ fontSize: '0.75rem' }} />;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  // Filter tickets based on search, invoice status, and ticket status
  const filteredTickets = tickets.filter(ticket => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const ticketNumber = (ticket.orgTicketNumber || ticket.org_ticket_number || ticket.ticketNumber || ticket.ticket_number || '').toString().toLowerCase();
      const serviceName = (ticket.serviceName || ticket.service_name || '').toLowerCase();
      const customerName = (ticket.customerName || ticket.customer_name || '').toLowerCase();
      if (!ticketNumber.includes(search) && !serviceName.includes(search) && !customerName.includes(search)) {
        return false;
      }
    }

    // Invoice status filter
    const isInvoiced = isTicketInvoiced(ticket.id);
    if (invoiceFilter === 'not_invoiced' && isInvoiced) return false;
    if (invoiceFilter === 'invoiced' && !isInvoiced) return false;

    // Ticket status filter
    if (statusFilter) {
      const ticketStatus = ticket.status?.toUpperCase();
      if (ticketStatus !== statusFilter) return false;
    }

    return true;
  });

  const availableTickets = filteredTickets.filter(
    t => !isTicketInvoiced(t.id)
  );

  const selectedTotal = tickets
    .filter(t => selectedTickets.includes(t.id))
    .reduce((sum, t) => sum + (parseFloat(t.totalCost || t.total_cost) || 0), 0);

  const disablePrev = page === 0;
  const disableNext = lastFetchCount < PAGE_SIZE;

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header - Mobile Responsive */}
      <Box display="flex" alignItems="center" mb={isMobile ? 2 : 3}>
        <IconButton onClick={() => navigate('/invoices')} sx={{ mr: 1 }}>
          <BackIcon />
        </IconButton>
        <Box flex={1}>
          <Typography variant={isSmallMobile ? 'h6' : 'h5'} fontWeight={600}>
            Create New Invoice
          </Typography>
          {!isSmallMobile && (
            <Typography variant="body2" color="text.secondary">
              Select an organization and tickets to create an invoice
            </Typography>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Invoice Configuration Warning - Mobile Responsive */}
      {hasInvoiceConfig === false && !checkingConfig && (
        <Paper sx={{ p: isMobile ? 2 : 4, mb: isMobile ? 2 : 3, bgcolor: 'action.hover', border: '1px solid', borderColor: 'warning.main' }}>
          <Box display="flex" alignItems={isMobile ? 'center' : 'flex-start'} gap={isMobile ? 1.5 : 2}>
            <WarningIcon sx={{ color: '#f57c00', fontSize: isMobile ? 32 : 40, mt: isMobile ? 0 : 0.5 }} />
            <Box flex={1}>
              <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="warning.dark" gutterBottom>
                Invoice Configuration Required
              </Typography>
              {!isSmallMobile && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Before creating invoices, you need to configure the invoice settings for this typing center.
                  This includes company details, logo, TRN number, and invoice number format.
                </Typography>
              )}
              <Button
                variant="contained"
                color="warning"
                size={isMobile ? 'small' : 'medium'}
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/settings?section=documents&tab=invoices')}
                fullWidth={isSmallMobile}
              >
                {isSmallMobile ? 'Configure Settings' : 'Configure Invoice Settings'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Loading Config Check */}
      {checkingConfig && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Organization Selection - Only show when config exists */}
      {hasInvoiceConfig && (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {isPlatform && (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Typing Center</InputLabel>
                <Select
                  value={selectedTypingCenterId}
                  label="Typing Center"
                  onChange={(e) => setSelectedTypingCenterId(e.target.value)}
                >
                  {typingCenters.map(tc => (
                    <MenuItem key={tc.id} value={tc.id}>
                      {tc.name} ({tc.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={isPlatform ? 6 : 12} md={isPlatform ? 8 : 12}>
            <FormControl fullWidth>
              <InputLabel>Select Organization</InputLabel>
              <Select
                value={selectedOrganization?.id || ''}
                label="Select Organization"
                onChange={handleOrganizationChange}
                disabled={loadingOrgs || mappedOrganizations.length === 0}
                sx={{
                  backgroundColor: selectedOrganization ? 'action.selected' : 'inherit',
                  '& .MuiSelect-select': {
                    fontWeight: selectedOrganization ? 600 : 400
                  }
                }}
              >
                {loadingOrgs ? (
                  <MenuItem value="" disabled>Loading organizations...</MenuItem>
                ) : mappedOrganizations.length === 0 ? (
                  <MenuItem value="" disabled>No mapped organizations found</MenuItem>
                ) : (
                  mappedOrganizations.map(org => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name} {org.code && `(${org.code})`}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
      )}

      {/* Tickets Section */}
      {hasInvoiceConfig && selectedOrganization && (
        <Paper sx={{ p: isMobile ? 2 : 3 }}>
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
                  Date Range:
                </Typography>
                <ButtonGroup size="small" variant="outlined">
                  {[
                    { value: '24_HR', label: '24 HR' },
                    { value: '7_DAYS', label: '7 DAYS' },
                    { value: '30_DAYS', label: '30 DAYS' },
                    { value: '90_DAYS', label: '90 DAYS' },
                    { value: 'CUSTOM', label: 'CUSTOM' }
                  ].map((option) => (
                    <Button
                      key={option.value}
                      onClick={() => {
                        if (option.value === 'CUSTOM') {
                          setCustomDateOpen(true);
                        } else {
                          setDateRange(option.value);
                          setPage(0);
                        }
                      }}
                      sx={{
                        fontSize: '0.75rem',
                        px: 1.5,
                        bgcolor: dateRange === option.value ? 'secondary.main' : 'transparent',
                        color: dateRange === option.value ? 'secondary.contrastText' : 'secondary.main',
                        borderColor: 'secondary.main',
                        '&:hover': {
                          bgcolor: dateRange === option.value ? 'secondary.dark' : 'action.hover',
                          borderColor: 'secondary.main'
                        }
                      }}
                    >
                      {option.label}
                    </Button>
                  ))}
                </ButtonGroup>
                {dateRange === 'CUSTOM' && customStartDate && customEndDate && (
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                    {customStartDate} to {customEndDate}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          <Dialog open={customDateOpen} onClose={() => setCustomDateOpen(false)}>
            <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>Select Date Range</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCustomDateOpen(false)} sx={{ color: 'text.secondary' }}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (customStartDate && customEndDate) {
                    setDateRange('CUSTOM');
                    setPage(0);
                    setCustomDateOpen(false);
                  }
                }}
                variant="contained"
                disabled={!customStartDate || !customEndDate}
              >
                Apply
              </Button>
            </DialogActions>
          </Dialog>

          {/* Tickets Header */}
          <Box
            display="flex"
            flexDirection={isSmallMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems={isSmallMobile ? 'stretch' : 'center'}
            gap={isSmallMobile ? 1.5 : 0}
            mb={2}
          >
            <Box mb={isSmallMobile ? 1 : 0}>
              <Typography variant={isMobile ? 'body1' : 'subtitle1'} fontWeight={600}>
                Tickets for {selectedOrganization.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Select tickets to include in the invoice
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={handleSelectAllAvailable}
                sx={{ fontSize: isSmallMobile ? '0.7rem' : '0.8rem' }}
              >
                Select All ({availableTickets.length})
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDeselectAll}
                sx={{
                  borderColor: '#666',
                  color: 'text.primary',
                  fontSize: isSmallMobile ? '0.7rem' : '0.8rem',
                  '&:hover': { borderColor: '#333', backgroundColor: 'action.hover' }
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          {/* Filters Row - Mobile Responsive */}
          <Grid container spacing={isMobile ? 1 : 2} mb={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                placeholder={isMobile ? "Search..." : "Search by ticket #, service, customer..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{isMobile ? 'Invoice' : 'Invoice Status'}</InputLabel>
                <Select
                  value={invoiceFilter}
                  label={isMobile ? 'Invoice' : 'Invoice Status'}
                  onChange={(e) => setInvoiceFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="not_invoiced">Not Invoiced</MenuItem>
                  <MenuItem value="invoiced">Invoiced</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{isMobile ? 'Status' : 'Ticket Status'}</InputLabel>
                <Select
                  value={statusFilter}
                  label={isMobile ? 'Status' : 'Ticket Status'}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="CLOSED">Closed</MenuItem>
                  <MenuItem value="OPEN">Open</MenuItem>
                  <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                  <MenuItem value="ASSIGNED">Assigned</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {!isMobile && (
              <Grid item xs={12} sm={2}>
                <Button
                  fullWidth
                  size="small"
                  variant="text"
                  onClick={() => {
                    setSearchTerm('');
                    setInvoiceFilter('not_invoiced');
                    setStatusFilter('');
                  }}
                  sx={{ height: '40px', color: '#666' }}
                >
                  Reset Filters
                </Button>
              </Grid>
            )}
          </Grid>

          {/* Tickets - Mobile Cards or Desktop Table */}
          {loadingTickets ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : filteredTickets.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary">
                {tickets.length === 0 ? 'No tickets found for this organization.' : 'No tickets match your search.'}
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Mobile Card View */
            <Stack spacing={1} sx={{ maxHeight: 'calc(100vh - 450px)', overflowY: 'auto' }}>
              {filteredTickets.map((ticket) => {
                const isInvoiced = isTicketInvoiced(ticket.id);
                const invoiceInfo = isInvoiced ? getInvoiceInfo(ticket.id) : null;
                const isSelectable = !isInvoiced;
                const isSelected = selectedTickets.includes(ticket.id);

                return (
                  <Card
                    key={ticket.id}
                    variant="outlined"
                    onClick={() => isSelectable && handleToggleTicket(ticket.id)}
                    sx={{
                      cursor: isSelectable ? 'pointer' : 'default',
                      opacity: isInvoiced ? 0.5 : 1,
                      backgroundColor: isSelected ? 'rgba(87, 148, 242, 0.08)' : 'inherit',
                      border: isSelected ? '2px solid #5794F2' : '1px solid #e0e0e0',
                      '&:active': isSelectable ? { backgroundColor: 'rgba(87, 148, 242, 0.12)' } : {}
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="flex-start" gap={1}>
                        <Checkbox
                          checked={isSelected}
                          disabled={!isSelectable}
                          onChange={() => handleToggleTicket(ticket.id)}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                          sx={{ p: 0, mt: 0.5 }}
                        />
                        <Box flex={1}>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                            <Box>
                              <Typography fontWeight={600} sx={{ fontSize: '0.9rem' }}>
                                #{ticket.orgTicketNumber || ticket.org_ticket_number || ticket.ticketNumber || ticket.ticket_number}
                              </Typography>
                              <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {ticket.serviceName || ticket.service_name}
                              </Typography>
                            </Box>
                            <Box textAlign="right">
                              {getTicketStatusChip(ticket.status)}
                            </Box>
                          </Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {ticket.customerName || ticket.customer_name || ticket.applicantName || '-'}
                            </Typography>
                            <Typography fontWeight={700} color="primary" sx={{ fontSize: '0.95rem' }}>
                              {formatCurrency(ticket.totalCost || ticket.total_cost)}
                            </Typography>
                          </Box>
                          {isInvoiced && invoiceInfo && (
                            <Box mt={0.5}>
                              <Chip
                                label={`Invoiced: ${invoiceInfo.invoice_number}`}
                                size="small"
                                color="success"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/invoices/${invoiceInfo.invoice_id}`);
                                }}
                                sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            /* Desktop Table View */
            <TableContainer sx={{ maxHeight: 'calc(100vh - 500px)', minHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: 'action.hover', color: 'text.primary', fontWeight: 600, fontSize: '0.85rem' } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedTickets.length > 0 && selectedTickets.length === availableTickets.length}
                        indeterminate={selectedTickets.length > 0 && selectedTickets.length < availableTickets.length}
                        onChange={(e) => e.target.checked ? handleSelectAllAvailable() : handleDeselectAll()}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Ticket #</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="right">Service Chg</TableCell>
                    <TableCell align="right">Govt Chg</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="center">Invoiced</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map((ticket) => {
                    const isInvoiced = isTicketInvoiced(ticket.id);
                    const invoiceInfo = isInvoiced ? getInvoiceInfo(ticket.id) : null;
                    const isSelectable = !isInvoiced;
                    const isSelected = selectedTickets.includes(ticket.id);

                    return (
                      <TableRow
                        key={ticket.id}
                        hover
                        onClick={() => isSelectable && handleToggleTicket(ticket.id)}
                        sx={{
                          cursor: isSelectable ? 'pointer' : 'default',
                          opacity: isInvoiced ? 0.5 : 1,
                          backgroundColor: isSelected ? 'rgba(87, 148, 242, 0.08)' : 'inherit'
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ py: 1 }}>
                          <Checkbox
                            checked={isSelected}
                            disabled={!isSelectable}
                            onChange={() => handleToggleTicket(ticket.id)}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                          />
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {ticket.orgTicketNumber || ticket.org_ticket_number || ticket.ticketNumber || ticket.ticket_number}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>
                            {ticket.serviceName || ticket.service_name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>
                            {ticket.customerName || ticket.customer_name || ticket.applicantName || ticket.applicant_name || ticket.personName || ticket.person_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {getTicketStatusChip(ticket.status)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>
                            {formatCurrency(ticket.serviceCharge || ticket.service_charge || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>
                            {formatCurrency(ticket.governmentFees || ticket.government_fees || ticket.govtCharge || ticket.govt_charge || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                            {formatCurrency(ticket.totalCost || ticket.total_cost)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1 }}>
                          {isInvoiced && invoiceInfo ? (
                            <Tooltip title={`Invoiced by ${invoiceInfo.organization_name || 'this organization'} - Click to view`}>
                              <Chip
                                label={invoiceInfo.invoice_number}
                                size="small"
                                color="success"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/invoices/${invoiceInfo.invoice_id}`);
                                }}
                                sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                              />
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>-</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={disablePrev}
              sx={{
                borderColor: '#666',
                color: 'text.primary',
                WebkitTextFillColor: '#333',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: 'background.paper',
                '&:hover': { borderColor: '#333', backgroundColor: 'action.hover' },
                '&.Mui-disabled': {
                  opacity: 1,
                  color: 'rgba(51, 51, 51, 0.35)',
                  WebkitTextFillColor: 'rgba(51, 51, 51, 0.35)',
                  borderColor: 'rgba(102, 102, 102, 0.35)'
                }
              }}
            >
              Prev
            </Button>
            <Typography sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
              Page {page + 1}
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setPage((p) => p + 1)}
              disabled={disableNext}
              sx={{
                borderColor: '#666',
                color: 'text.primary',
                WebkitTextFillColor: '#333',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: 'background.paper',
                '&:hover': { borderColor: '#333', backgroundColor: 'action.hover' },
                '&.Mui-disabled': {
                  opacity: 1,
                  color: 'rgba(51, 51, 51, 0.35)',
                  WebkitTextFillColor: 'rgba(51, 51, 51, 0.35)',
                  borderColor: 'rgba(102, 102, 102, 0.35)'
                }
              }}
            >
              Next
            </Button>
          </Box>

          {/* Summary Footer - Mobile Responsive */}
          <Box sx={{ mt: 2, p: isMobile ? 1.5 : 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
            {isMobile ? (
              /* Mobile Summary Layout */
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1.5}>
                  <Typography variant="caption" color="text.secondary">
                    Total: <strong>{tickets.length}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Available: <strong>{availableTickets.length}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Invoiced: <strong>{invoicedTickets.length}</strong>
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1.5 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary">Selected</Typography>
                    <Typography variant="h6" fontWeight={700}>{selectedTickets.length}</Typography>
                  </Box>
                  <Box textAlign="right">
                    <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary">
                      {formatCurrency(selectedTotal)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              /* Desktop Summary Layout */
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Total: <strong>{tickets.length}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Available: <strong>{availableTickets.length}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="text.secondary">
                        Invoiced: <strong>{invoicedTickets.length}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={3}>
                    <Box textAlign="right">
                      <Typography variant="body2" color="text.secondary">
                        Selected Tickets
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {selectedTickets.length}
                      </Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="body2" color="text.secondary">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="primary">
                        {formatCurrency(selectedTotal)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>

          {/* Action Buttons - Mobile Responsive */}
          <Box
            display="flex"
            flexDirection={isSmallMobile ? 'column-reverse' : 'row'}
            justifyContent="flex-end"
            gap={isSmallMobile ? 1 : 2}
            mt={isMobile ? 2 : 3}
          >
            <Button
              variant="outlined"
              fullWidth={isSmallMobile}
              onClick={() => navigate('/invoices')}
              sx={{
                borderColor: '#666',
                color: 'text.primary',
                '&:hover': { borderColor: '#333', backgroundColor: 'action.hover' }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size={isSmallMobile ? 'medium' : 'large'}
              fullWidth={isSmallMobile}
              startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <InvoiceIcon />}
              onClick={handleCreateInvoice}
              disabled={creating}
            >
              {creating ? 'Creating...' : isSmallMobile ? `Create (${selectedTickets.length})` : `Create Invoice (${selectedTickets.length} tickets)`}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Empty State - Only show when config exists but no org selected */}
      {hasInvoiceConfig && !selectedOrganization && (
        <Paper sx={{ p: isMobile ? 4 : 6, textAlign: 'center' }}>
          <InvoiceIcon sx={{ fontSize: isMobile ? 48 : 64, color: 'text.disabled', mb: isMobile ? 1.5 : 2 }} />
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} color="text.secondary" gutterBottom>
            Select an Organization
          </Typography>
          <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary">
            Choose an organization from the dropdown above to view and select tickets for the invoice.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CreateInvoice;
